import { api, writeDB } from './store';
import { TimeFrame, formatDate, parseDate, present } from '../util/util';
import { Company, Address } from './company';
import { history } from './history';
import { orderPath } from '../routes';
import { Delivery, DeliveryStatus } from './delivery';
import { User } from './user';
import { Invoice, InvoiceStatus } from './invoice';
import {
  Collection,
  PaginatedLoaderResponse,
  paginatedLoaderResponseFrom,
  SequentialPollingState,
  useSequentialPolling,
} from './data';
import { Payment, PaymentMethodId } from './payment';
import { BasePermissions } from './permission';
import fileDownload from 'js-file-download';
import { sum, compact } from 'lodash';
import legacy from 'legacy-encoding';
import { apollo } from './graphql';
import { ProductVariant, Product } from './product';
import { ORDER_ITEM_GROUPS } from './queries/order';
import { VATRatePerCountry } from './price';
import { useEffect, useRef } from 'react';
import { decamelize } from 'humps';

/* INTERFACES */
export type OrderStatus =
  | 'draft'
  | 'payment_pending'
  | 'fulfillment'
  | 'done'
  | 'canceled'
  | 'problem'
  | 'refund_pending'
  | 'incomplete'
  | 'cart'
  | 'pending_fulfillment';

export type OrderType = 'merch_clearing' | 'event_clearing' | 'order';

type ResaleAvailability = 'not_available' | 'available_with_refund' | 'available_with_transfer';

export interface ItemGroup {
  id: string;
  count: number;
  sku?: string;
  deliveryId: string | null;
  product?: Partial<Product>;
  productId?: string;
  productVariantId?: string;
  productVariant?: Partial<ProductVariant>;
  discountAmount?: number;
  invalidatingIds?: string[];
  itemIds: string[];
  invalidatedByIds?: string[];
  refundingIds?: string[];
  refundedByIds?: string[];
  shop: {
    id: string;
    name: string;
  } | null;
  permissions: {
    refund: boolean;
    remove: boolean;
    changeDelivery: boolean;
    changeInvoice: boolean;
  };
  voucherCode?: {
    code: string;
    voucherCampaign: {
      title: string;
    };
  };
  // TODO: KS-1236 - Re implement OutgoingItemGroup and IncomingItemGroup with the correct properties for each
  invalidated: boolean;
  invoiceId: string | null;
  tickets?: Ticket[];
  price?: number | null;
  vat: number | null;
  total: number | null;
  vatPercentage: number | null;
  personalIdentifier?: string | null;
}

export interface OutgoingItemGroup extends ItemGroup {
  __typename: 'OutgoingItemGroup';
}

export interface IncomingItemGroup extends ItemGroup {
  __typename: 'IncomingItemGroup';
}

export interface Ticket {
  id: string;
  personalIdentifier?: string | null;
  seat?: {
    id?: string;
    name?: string;
    row?: string;
    venueSectionId?: string;
  } | null;
  url: string | null;
  validationCode?: string | null;
  ticketPassword?: string | null;
  usedAt?: string | null;
  validatedBy?: string | null;
  passbook?: {
    id: string;
    url: string;
  };
  resaleOffer?: {
    code: string;
    status: string;
    public: boolean;
    privateUrl: string | null;
    newOrderId: string | null;
    newOrderReference: string | null;
  };
  resoldByOffer?: {
    originalOrderId: string;
    originalOrderReference: string;
    soldItemValidationCode: string;
  };
  resaleAvailability: ResaleAvailability;
  permissions: {
    offerForResale: boolean;
  };
}

export interface Order {
  autoCancelAt?: Date;
  cancelAt?: Date;
  balance: number;
  company: Company;
  createdAt?: Date;
  deliveries: Delivery[];
  id: string;
  invoices: Invoice[];
  itemGroups?: Array<OutgoingItemGroup | IncomingItemGroup>;
  orderedAt?: string;
  payments: Payment[];
  preferredPaymentMethod?: PaymentMethodId;
  preferredShippingMethodId?: string;
  preferredBillingAddress: Address | null;
  preferredShippingAddress: Address | null;
  reference: string;
  status: OrderStatus;
  tags: string[];
  updatedAt: Date;
  user?: User;
  type: OrderType;
  $permissions: Array<BasePermissions | 'create_clearing' | 'resend_online_tickets' | 'resend_confirmation_email'>;
  problem?: boolean;
  shopId?: string;
  paymentLink?: string;
  voucherCode?: {
    code: string;
    voucherCampaign: {
      title: string;
      amount: number;
      discountType: string;
    };
  };
}

export type RefundOption = 'refund-release' | 'refund-block' | 'refund-destroy' | 're-issue-tickets';

export interface RefundModel {
  option?: RefundOption;
  blockReason?: string;
  markDeliveryAsSent?: boolean;
}

/* ORDERS CREATE */
export async function createOrder(model: Collection & { itemType?: string }): Promise<void> {
  const response = await api.createOrder({
    [model.itemType === 'user' ? 'userId' : 'companyId']: model.id,
  });

  writeDB.table('orders').insert(response.data);
  history.push(orderPath(response.data.id));
}

/* ORDERS READ */
export async function getOrders({
  term,
  page,
  perPage,
  deliveryStatus,
  status,
  storage,
  timeframe,
  sortBy = 'created_at',
  sortDir = 'desc',
  withShippableProductsOnly,
  archived,
  paymentMethod,
  productId,
  productVariantId,
  venueSectionId,
  itemStatus,
  containingOnly,
}: {
  term?: string;
  page?: number;
  perPage?: number;
  deliveryStatus?: string[];
  status?: string[];
  storage?: string[];
  timeframe?: TimeFrame;
  sortBy?: string;
  sortDir?: string;
  withShippableProductsOnly?: boolean;
  archived?: boolean;
  paymentMethod?: string[];
  productId?: string;
  productVariantId?: string;
  venueSectionId?: string;
  itemStatus?: string;
  containingOnly?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexOrders({
    page: (page || 0) + 1,
    perPage,
    term,
    deliveryStatus,
    paymentMethod,
    status,
    storage,
    ...(timeframe && timeframe[0] && timeframe[1]
      ? {
          startDate: timeframe[0],
          endDate: timeframe[1],
        }
      : {}),
    sortBy,
    sortDir,
    withShippableProductsOnly,
    archived,
    productId,
    productVariantId,
    venueSectionId,
    itemStatus: itemStatus ? decamelize(itemStatus) : undefined,
    containingOnly,
  });
  writeDB.table('orders').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getOrder(orderId: string): Promise<Order> {
  const order = (await api.showOrder({ orderId })).data;

  writeDB.table('orders').upsert({
    ...order,
  });
  return order as unknown as Order;
}

export async function loadItemGroups({ id }: { id: string }): Promise<void> {
  const result = await apollo.query({
    query: ORDER_ITEM_GROUPS,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  // this is a result of the graphql returning an untyped json for the taxes.
  const itemGroups = result.data?.order?.itemGroups.map((group) => ({
    ...group,
    product: {
      ...group.product,
      taxesPerCountry: group.product.taxesPerCountry as unknown as VATRatePerCountry,
      dhlShippingFrom: parseDate(group.product.dhlShippingFrom),
    },
  }));

  if (itemGroups) {
    // TODO: Fix api model
    const data = { itemGroups } as unknown as Order;
    writeDB.table('orders').update(id, data);
  }
}

export async function loadOrderWithItemGroups(id: string): Promise<void> {
  const order = await getOrder(id);
  if (order.type !== 'merch_clearing') {
    await loadItemGroups({ id });
  }
}

export async function refreshOrderStatus(orderId: string): Promise<Order> {
  const order = await api.recalculateStatusOrder({ orderId });

  writeDB.table('orders').upsert({
    ...order,
  });
  return order as unknown as Order;
}

/* ORDERS UPDATE */
export async function updateOrder({
  orderId,
  comment,
  tags,
  problem,
  userId,
  cancelAt,
}: {
  orderId: string;
  comment?: string;
  problem?: boolean;
  tags?: string[];
  userId?: string | null;
  cancelAt?: Date | null;
}): Promise<void> {
  const response = await api.updateOrder({
    orderId,
    comment,
    tags,
    userId,
    problem,
    cancelAt,
  });
  // TODO: Update api model to match local interface
  const data = response.data as unknown as Order;
  writeDB.table('orders').update(response.data.id, data);
}

export async function updateOrders(orders: Order[]): Promise<void> {
  const apiPromises: Array<Promise<{ data: Partial<Order> }>> = [];
  orders.forEach((order) => {
    // TODO: Update api model to match local interface
    // eslint-disable-next-line
    // @ts-ignore
    apiPromises.push(api.updateOrder({ orderId: order.id, tags: order.tags }));
  });
  const response = await Promise.all(apiPromises);
  response.forEach((respItem) => {
    if (respItem.data.id) {
      writeDB.table('orders').update(respItem.data.id, respItem.data);
    }
  });
}

export async function createClearing({ orderId, from, to }: { orderId: string; from: Date; to: Date }): Promise<void> {
  await api.createClearing({ orderId, from, to });
  await getOrder(orderId);
}

/* ORDERS DELETE */
export async function deleteOrder({ orderId }: { orderId: string }): Promise<void> {
  await api.destroyOrder({ orderId });
  writeDB.table('orders').delete(orderId);
}

/* HELPERS */
export function colorForOrderStatus(status?: OrderStatus | DeliveryStatus | InvoiceStatus): string | undefined {
  if (!status) {
    return '#000000';
  }

  const options = {
    draft: '#F35549',
    paymentPending: 'var(--color-ticket-cart)',
    pending: 'var(--color-ticket-cart)',
    fulfillment: 'var(--color-success)',
    done: 'var(--color-input-underline)',
    canceled: '#343434',
  };

  return options[status as keyof typeof options];
}

export async function printShippingLabels(orders: Order[], deliveryIds: string[]): Promise<void> {
  let namedOrder: Order | undefined;
  const lines = deliveryIds.map((deliveryId) => {
    const order = orders.find((e) => !!e.deliveries.find((f) => f.id === deliveryId));
    if (!order) {
      throw new Error(`Missing order for delivery id ${deliveryId}`);
    }
    namedOrder = order;
    const delivery = order.deliveries.find((e) => e.id === deliveryId);
    if (!delivery) {
      throw new Error(`Missing delivery for delivery id ${deliveryId}`);
    }

    const productsMaxDHLDate = order.itemGroups
      ?.map((item) => item.product?.dhlShippingFrom)
      .reduce((oldValue, newValue) => {
        return new Date(Math.max(oldValue?.getTime() || 0, newValue?.getTime() || 0));
      });
    const labelMaxDate = new Date(Math.max(productsMaxDHLDate?.getTime() || 0, new Date().getTime()));

    const address = delivery.address;
    const weight = Math.max(
      sum(
        order.itemGroups
          ?.filter((e) => e.deliveryId === deliveryId)
          .map((e) => (e.productVariant && e.productVariant.weight) || 0)
      ) * 0.001,
      0.1
    );

    return [
      order.reference,
      address.firstName,
      address.lastName,
      address.company,
      address.address,
      address.address2,
      address.postCode,
      address.city,
      '',
      weight.toFixed(2).replace('.', ','),
      [address.firstName, address.lastName].filter(Boolean).join(' '),
      formatDate(labelMaxDate, 'de'),
    ]
      .map((e) => (e || '').replace(/;/, ','))
      .join(';');
  });
  let name = 'DHL-labels.txt';
  if (lines.length === 1 && namedOrder) {
    name = `DHL-${namedOrder.reference}.txt`;
  }
  fileDownload(legacy.encode(lines.join('\n'), 'iso-8859-1'), name);
  // eslint-disable-next-line
  console.log(name, lines);
}

export async function sendRefundEmail({
  orderId,
  productId,
  email,
}: {
  orderId: string;
  productId?: string;
  email?: string;
}): Promise<void> {
  await api.sendRefundEmail({ orderId, productId, email });
}

export function isClearingOrder(order: Order): boolean {
  return order.type === 'merch_clearing' || order.type === 'event_clearing';
}

function getTicketsWithoutUrlsFromOrder(order: Order): Ticket[] {
  const itemGroups = (order.itemGroups || []) as Array<OutgoingItemGroup | IncomingItemGroup>;
  return compact(
    itemGroups.flatMap((g) => {
      if (!g.tickets) return [];

      const invoice = order.invoices.find((invoice) => invoice.id === g.invoiceId);
      if (invoice?.status !== 'booked') return [];

      return g.tickets.filter((t) => !present(t.url));
    })
  );
}

function orderIsMissingTicketUrls(order: Order): boolean {
  return !!getTicketsWithoutUrlsFromOrder(order).length;
}

export function useTicketUrlPollUntilAllHaveUrl(order: Order): SequentialPollingState {
  const orderId = order.id;
  const polling = useSequentialPolling({
    load: async () => {
      await loadOrderWithItemGroups(orderId);
      return writeDB.table('orders').find(order.id)!;
    },
    interval: 5,
    onUpdate: (o) => orderIsMissingTicketUrls(o),
  });

  const orderRef = useRef(order);
  orderRef.current = order;

  // Auto-Start or restart the polling when order is missing ticket URLs (ex: booking invoice).
  useEffect(() => {
    if (orderIsMissingTicketUrls(orderRef.current)) {
      polling.start();
    }
  }, [orderIsMissingTicketUrls(order)]);

  return polling;
}
